/**
 * Exception requests (workbench D379), on the Policies page: where the policy refused someone a model, a runtime, a
 * tool server or an address, they ask for an exception from where it happened. Administrators answer here: what waits
 * first (the oldest at the top), with Approve (choose how long, never longer than asked) and Decline (a reason the
 * person reads); then what was answered, with Revoke on an exception still running. Last, the organisation's switch.
 * The same requests reach administrators in their Inbox and on the phone.
 */

import { approvalLengths, describeSubject, exceptionDurationWords, type ExceptionRequestAdminView } from '@aioe/shared';
import { Clock, ShieldExclamation } from '@gravity-ui/icons';
import { Input, Label, TextField } from '@heroui/react';
import { useState } from 'react';

import { problemText, routes } from '../api/client.ts';
import { dateTimeWords, timeAgo, useLoad } from '../hooks.ts';
import { Button, ChoiceSelect, ConfirmDialog, Empty, ErrorNote, inputClass, ListBlock, ListGroup, ListRow, StatusMark, Toggle } from './ui.tsx';
import { RowMenu } from './ui/RowMenu.tsx';
import { VIEW_TONES } from './ui/Art.tsx';

const tone = VIEW_TONES.policies!;
/** How many answered requests are listed; the rest stay in the audit. */
const ANSWERED_SHOWN = 50;

const upper = (s: string) => s.charAt(0).toUpperCase() + s.slice(1);
const nameOf = (r: ExceptionRequestAdminView) => r.askedBy?.name ?? r.askedBy?.principal ?? 'Someone';
const placeOf = (r: ExceptionRequestAdminView) => [r.where?.repository ? `in ${r.where.repository}` : '', r.where?.machine ? `on ${r.where.machine}` : ''].filter(Boolean).join(' ');

/** Where an answered request stands, as a dot and a word. */
function standing(r: ExceptionRequestAdminView): { state: Parameters<typeof StatusMark>[0]['state']; label: string } {
  if (r.revoked) return { state: 'offline', label: 'Ended early' };
  switch (r.state) {
    case 'approved':
      return { state: 'connected', label: 'Approved' };
    case 'declined':
      return { state: 'disabled', label: 'Declined' };
    case 'withdrawn':
      return { state: 'idle', label: 'Withdrawn' };
    case 'expired':
      return { state: 'offline', label: 'Ended' };
    default:
      return { state: 'medium', label: 'Waiting' };
  }
}

/** The answer in a sentence: "Approved by Jean-Pierre 3 h ago, until Fri 31 Oct, 2:30 pm: a day while we review it". */
function answerWords(r: ExceptionRequestAdminView): string {
  const by = r.answer?.by?.name ? ` by ${r.answer.by.name}` : '';
  const note = r.answer?.note ? `: ${r.answer.note}` : '';
  if (r.revoked) return `Ended early${r.revoked.by?.name ? ` by ${r.revoked.by.name}` : ''} ${timeAgo(r.revoked.at)}${r.revoked.reason ? `: ${r.revoked.reason}` : ''}`;
  switch (r.state) {
    case 'approved':
      return `Approved${by} ${r.answer ? timeAgo(r.answer.at) : ''}, until ${r.answer?.until ? dateTimeWords(r.answer.until) : 'its end'}${note}`;
    case 'expired':
      return `Approved${by}; ended ${r.answer?.until ? dateTimeWords(r.answer.until) : ''}`;
    case 'declined':
      return `Declined${by} ${r.answer ? timeAgo(r.answer.at) : ''}${note}`;
    case 'withdrawn':
      return `Withdrawn by ${nameOf(r)}`;
    default:
      return '';
  }
}

/** The administrators' list, loaded once by the page so its numbers and this view agree. */
export function useExceptionRequests() {
  return useLoad(() => routes.exceptionRequests(), [], 60_000);
}

export function ExceptionRequests({ list }: { list: ReturnType<typeof useExceptionRequests> }) {
  const org = useLoad(() => routes.organisationProfile(), []);
  const [approving, setApproving] = useState<ExceptionRequestAdminView>();
  const [hours, setHours] = useState<string>();
  const [note, setNote] = useState('');
  const [declining, setDeclining] = useState<string>();
  const [reason, setReason] = useState('');
  const [revoking, setRevoking] = useState<ExceptionRequestAdminView>();
  const [revokeReason, setRevokeReason] = useState('');
  const [busy, setBusy] = useState<string>();
  const [error, setError] = useState<string>();

  const requests = list.data?.requests ?? [];
  const pending = requests.filter((r) => r.state === 'pending');
  const answered = requests.filter((r) => r.state !== 'pending');
  const on = org.data?.exceptionRequests ?? true;

  const run = async (id: string, f: () => Promise<unknown>) => {
    setBusy(id);
    setError(undefined);
    try {
      await f();
      setDeclining(undefined);
      setReason('');
      list.reload();
    } catch (e) {
      setError(problemText(e));
    } finally {
      setBusy(undefined);
    }
  };

  const openApprove = (r: ExceptionRequestAdminView) => {
    setApproving(r);
    setHours(String(r.hours));
    setNote('');
  };

  return (
    <>
      <ErrorNote error={list.error} />
      <ListGroup
        title="Waiting for an answer"
        count={list.data ? pending.length : undefined}
        description="Asked from where the policy refused someone, oldest first. They wait in your Inbox too."
        empty={
          list.data && !pending.length ? (
            <Empty
              tone={tone}
              icon={<ShieldExclamation />}
              title="Nothing waiting"
              body={on ? 'When the policy refuses someone a model, a runtime, a tool server or an address, they can ask for an exception from where it happened. Their requests wait here, and in your Inbox.' : 'People cannot ask for exceptions at the moment: taking requests is turned off below.'}
            />
          ) : undefined
        }
      >
        {pending.map((r) => (
          <ListRow
            key={r.id}
            icon={<ShieldExclamation />}
            title={`${nameOf(r)} asks for an exception for ${describeSubject(r.subject)} ${exceptionDurationWords(r.hours)}`}
            description={
              <>
                <span className="block">{[r.projectName ?? r.projectId, placeOf(r), timeAgo(r.askedAt)].filter(Boolean).join(' · ')}</span>
                <span className="block text-foreground">Why: “{r.why}”</span>
                {r.refusal ? <span className="block">The policy said: {r.refusal}</span> : null}
              </>
            }
            control={
              declining !== r.id ? (
                <>
                  <Button size="sm" variant="secondary" isDisabled={Boolean(busy)} onPress={() => openApprove(r)}>
                    Approve
                  </Button>
                  <Button size="sm" variant="ghost" isDisabled={Boolean(busy)} onPress={() => setDeclining(r.id)}>
                    Decline
                  </Button>
                </>
              ) : undefined
            }
          >
            {declining === r.id && (
              <div className="grid gap-2 @[35rem]:flex @[35rem]:items-end">
                <label className="grid flex-1 gap-1 text-xs text-muted">
                  Why it is declined ({nameOf(r)} reads this)
                  <input className={inputClass} value={reason} onChange={(ev) => setReason(ev.target.value)} maxLength={2000} autoFocus />
                </label>
                <div className="flex gap-2">
                  <Button size="sm" variant="ghost" onPress={() => setDeclining(undefined)}>
                    Cancel
                  </Button>
                  <Button size="sm" variant="danger" isDisabled={!reason.trim()} isPending={busy === r.id} onPress={() => void run(r.id, () => routes.declineException(r.id, reason.trim()))}>
                    Decline
                  </Button>
                </div>
              </div>
            )}
          </ListRow>
        ))}
        {error ? (
          <ListBlock>
            <ErrorNote error={error} />
          </ListBlock>
        ) : null}
      </ListGroup>

      {answered.length ? (
        <ListGroup title="Answered" count={answered.length} description={answered.length > ANSWERED_SHOWN ? `The newest ${ANSWERED_SHOWN}; the audit keeps every one.` : 'Newest first.'}>
          {answered.slice(0, ANSWERED_SHOWN).map((r) => {
            const s = standing(r);
            const live = r.state === 'approved' && !r.revoked;
            return (
              <ListRow
                key={r.id}
                icon={<Clock />}
                title={`${upper(describeSubject(r.subject))}, for ${nameOf(r)}`}
                description={[r.projectName ?? r.projectId, answerWords(r)].filter(Boolean).join(' · ')}
                status={<StatusMark state={s.state} label={s.label} />}
                compactControl
                control={
                  <>
                    {live ? (
                      <RowMenu
                        label={`More for the exception for ${describeSubject(r.subject)}`}
                        items={[{ id: 'revoke', label: 'Revoke it…', danger: true, disabled: Boolean(busy) }]}
                        onAction={() => {
                          setRevoking(r);
                          setRevokeReason('');
                        }}
                      />
                    ) : null}
                  </>
                }
              />
            );
          })}
        </ListGroup>
      ) : null}

      <ListGroup title="Taking requests">
        <ListRow
          title="People may ask for an exception"
          description="Where the policy refuses them. Turned off, the workbench tells them to ask an administrator instead."
          control={
            <Toggle
              label="Take requests for exceptions"
              checked={on}
              isDisabled={!org.data}
              onChange={(v) => {
                setError(undefined);
                void routes.setExceptionRequests(v).then(org.reload, (e) => setError(problemText(e)));
              }}
            />
          }
        />
      </ListGroup>

      <ConfirmDialog
        isOpen={Boolean(approving)}
        title={approving ? `Approve an exception for ${describeSubject(approving.subject)}?` : 'Approve?'}
        confirmLabel="Approve"
        onCancel={() => setApproving(undefined)}
        onConfirm={async () => {
          const r = approving!;
          try {
            await routes.approveException(r.id, Number(hours ?? r.hours), note.trim() || undefined);
            setApproving(undefined);
            list.reload();
          } catch (e) {
            setApproving(undefined);
            setError(problemText(e));
          }
        }}
      >
        {approving ? (
          <>
            <p>
              Agents in {approving.projectName ?? 'the project'} may use it for {nameOf(approving)} until the time you choose; their workbench stops on its own then. You can revoke it sooner.
            </p>
            <div className="text-sm font-medium text-foreground">For how long</div>
            <ChoiceSelect label="For how long" className="w-full" value={hours} options={Object.fromEntries(approvalLengths(approving.hours).map((l) => [String(l.hours), l.words.charAt(0).toUpperCase() + l.words.slice(1)]))} onChange={setHours} />
            <TextField variant="secondary" value={note} onChange={setNote} maxLength={2000}>
              <Label>A note for {nameOf(approving)} (optional)</Label>
              <Input placeholder="While we review the vendor" />
            </TextField>
          </>
        ) : null}
      </ConfirmDialog>

      <ConfirmDialog
        isOpen={Boolean(revoking)}
        title={revoking ? `End the exception for ${describeSubject(revoking.subject)}?` : 'End it?'}
        confirmLabel="Revoke"
        danger
        onCancel={() => setRevoking(undefined)}
        onConfirm={async () => {
          const r = revoking!;
          try {
            await routes.revokeException(r.id, revokeReason.trim() || undefined);
            setRevoking(undefined);
            list.reload();
          } catch (e) {
            setRevoking(undefined);
            setError(problemText(e));
          }
        }}
      >
        {revoking ? (
          <>
            <p>
              {nameOf(revoking)}'s workbench stops using it at its next policy check, before {revoking.answer?.until ? dateTimeWords(revoking.answer.until) : 'its end'}. They are told, with your reason.
            </p>
            <TextField variant="secondary" value={revokeReason} onChange={setRevokeReason} maxLength={2000}>
              <Label>Why (optional)</Label>
              <Input placeholder="The review found a problem" />
            </TextField>
          </>
        ) : null}
      </ConfirmDialog>
    </>
  );
}
